import React, { useMemo, useState } from 'react';
import { useStudents } from '../../../lib/useStudents';

type Props = {
  value?: string;
  onChange: (id: string) => void;
  disabled?: boolean;
};

export default function StudentSelector({ value, onChange, disabled }: Props) {
  const { students, loading } = useStudents({ orderByLastName: true, ensureSubscription: true });
  const [yearGroup, setYearGroup] = useState<string>('');
  const [klass, setKlass] = useState<string>('');
  const [search, setSearch] = useState<string>('');

  const YEAR_ORDER = useMemo(() => ([
    'Reception',
    'Year 1',
    'Year 2',
    'Year 3',
    'Year 4',
    'Year 5',
    'Year 6',
    'Year 7',
    'Year 8',
    'Year 9',
    'Year 10',
    'Year 11',
  ]), []);

  const yearGroups = useMemo(() => {
    const set = new Set<string>();
    for (const s of students) if (s.yearGroup) set.add(s.yearGroup);
    return Array.from(set).sort((a, b) => {
      const ai = YEAR_ORDER.indexOf(a);
      const bi = YEAR_ORDER.indexOf(b);
      if (ai !== -1 && bi !== -1) return ai - bi;
      if (ai !== -1) return -1;
      if (bi !== -1) return 1;
      return String(a).localeCompare(String(b));
    });
  }, [students, YEAR_ORDER]);

  const classesInYear = useMemo(() => {
    const set = new Set<string>();
    for (const s of students) {
      if (yearGroup && s.yearGroup !== yearGroup) continue;
      const c = (s.className || s.class || s.tutorGroup || '').trim();
      if (c) set.add(c);
    }
    return Array.from(set).sort();
  }, [students, yearGroup]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return students.filter((s: any) => {
      // Enforce year group selection: only show names when a year is picked
      if (!yearGroup) return false;
      if (s.yearGroup !== yearGroup) return false;
      const c = (s.className || s.class || s.tutorGroup || '').trim();
      if (klass && c !== klass) return false;
      if (!q) return true;
      const name = `${s.firstName || ''} ${s.lastName || ''}`.toLowerCase();
      return name.includes(q);
    });
  }, [students, yearGroup, klass, search]);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Year group</label>
          <select
            value={yearGroup}
            onChange={(e) => { setYearGroup(e.target.value); setKlass(''); }}
            disabled={disabled || loading}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          >
            <option value="">All</option>
            {yearGroups.map((yg) => (
              <option key={yg} value={yg}>{yg}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Class</label>
          <select
            value={klass}
            onChange={(e) => setKlass(e.target.value)}
            disabled={disabled || loading || classesInYear.length === 0}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          >
            <option value="">All</option>
            {classesInYear.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Search by name</label>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="e.g. Jane Doe"
            disabled={disabled}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Select student</label>
        <select
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled || loading || !yearGroup || filtered.length === 0}
          className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
        >
          <option value="" disabled>{!yearGroup ? 'Select a year group first' : (loading ? 'Loading…' : (filtered.length ? 'Choose a student' : 'No matches'))}</option>
          {filtered.map((s: any) => (
            <option key={s.id} value={s.id}>{`${s.firstName || ''} ${s.lastName || ''}`.trim() || s.name || 'Unknown'}</option>
          ))}
        </select>
      </div>
    </div>
  );
}


